import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import './EditGasDistributorProjectModal.css';
import AppIcon from './icons/AppIcon';

/**
 * Modal de edição para projetos do tipo Gas Distributor
 * Permite visualizar conexão vinculada e selecionar grupo WhatsApp
 */
export default function EditGasDistributorProjectModal({ isOpen, onClose, project, onSave }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Estado da aba ativa
  const [activeTab, setActiveTab] = useState('geral');
  
  // Estados da conexão
  const [connection, setConnection] = useState(null);
  const [availableConnections, setAvailableConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [projectAgentId, setProjectAgentId] = useState(null);
  
  // Estados dos grupos
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Estados das tags
  const [tagsConfiguradas, setTagsConfiguradas] = useState([]);
  const [editingTag, setEditingTag] = useState(null);
  const [showTagForm, setShowTagForm] = useState(false);
  const [tagForm, setTagForm] = useState({
    nome: '',
    descricao: '',
    regra_aplicacao: '',
    desconto_percentual: '',
    cor: '#3B82F6',
    icone: 'label',
    ativa: true
  });

  // Estados do formulário de clientes
  const [formConfig, setFormConfig] = useState({
    campos_obrigatorios_cliente: {
      nome: true,
      telefone: true,
      endereco: false,
      numero: false,
      bairro: false,
      cidade: false,
      complemento: false,
      cep: false,
      forma_pagamento: false
    },
    campos_obrigatorios_endereco: {
      endereco: true,
      numero: true,
      bairro: true,
      cidade: true,
      complemento: false,
      cep: false
    }
  });

  // Estados das políticas
  const [politicas, setPoliticas] = useState({
    politica_entrega: '',
    politica_cancelamento: '',
    politica_vendas: ''
  });

  // Estados dos horários de funcionamento
  const [horariosFuncionamento, setHorariosFuncionamento] = useState({
    segunda: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    terca: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    quarta: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    quinta: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    sexta: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    sabado: { ativo: true, abertura: '08:00', fechamento: '12:00' },
    domingo: { ativo: false, abertura: '', fechamento: '' }
  });

  // Estados das formas de pagamento
  const [formasPagamento, setFormasPagamento] = useState([
    { tipo: 'dinheiro', ativo: true, observacoes: '', instrucoes: '' },
    { tipo: 'pix', ativo: true, tipoPix: 'chave', chavePix: '', observacoes: '', instrucoes: '' },
    { tipo: 'cartao', ativo: false, observacoes: '', instrucoes: '' }
  ]);
  const [editandoPagamento, setEditandoPagamento] = useState(null);
  const [mostrarAdicionarOutros, setMostrarAdicionarOutros] = useState(false);

  // Estados das restrições do agente
  const [restricoesAgente, setRestricoesAgente] = useState([]);
  const [editandoRestricao, setEditandoRestricao] = useState(null);
  const [modalRestricaoAberto, setModalRestricaoAberto] = useState(false);
  const [novaRestricao, setNovaRestricao] = useState({ titulo: '', descricao: '', ativo: true });

  // Estados das áreas de cobertura
  const [areasCobertura, setAreasCobertura] = useState([]);
  const [novaArea, setNovaArea] = useState({ bairro: '', taxa: '', tempoEntrega: '', naoAtendemos: false });
  const [cidadeBuscaBairros, setCidadeBuscaBairros] = useState('');
  const [ufBuscaBairros, setUfBuscaBairros] = useState('PR');
  const [taxaPadraoBairros, setTaxaPadraoBairros] = useState('');
  const [tempoPadraoBairros, setTempoPadraoBairros] = useState('');
  const [bairrosEncontrados, setBairrosEncontrados] = useState([]);
  const [bairrosSelecionados, setBairrosSelecionados] = useState([]);
  const [carregandoBairros, setCarregandoBairros] = useState(false);
  const [erroBuscaBairros, setErroBuscaBairros] = useState('');

  // Estados das instruções de atendimento
  const [instrucoesAtendimento, setInstrucoesAtendimento] = useState({
    promocoes_combos: { ativo: false, texto: '' },
    atendimento_inicial: { ativo: false, texto: '' },
    instrucoes_gerais: { ativo: false, texto: '' },
    pos_venda: { ativo: false, texto: '' },
    programa_indicacao: { ativo: false, texto: '' },
    negociacao_precos: { ativo: false, texto: '' }
  });

  // Estados das informações da empresa
  const [empresaData, setEmpresaData] = useState({
    nomeFantasia: '',
    descricao: '',
    enderecoBase: {
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: ''
    },
    telefoneEmpresa: '',
    emailContato: '',
    website: ''
  });

  // Carregar dados ao abrir modal
  useEffect(() => {
    if (isOpen && project) {
      loadProjectData();
    }
  }, [isOpen, project]);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      const projectCompanyId = project?.company_id || JSON.parse(localStorage.getItem('astro_user_data') || '{}')?.company_id;
      const currentConnectionId = project?.settings?.gas_distributor?.connection_id || null;

      let resolvedAgentId = project?.ai_agent_id || null;
      if (!resolvedAgentId) {
        const { data: projectAgentRow } = await supabase
          .from('gas_distributor_agents')
          .select('agente_id')
          .eq('project_id', project.id)
          .eq('ativo', true)
          .maybeSingle();
        resolvedAgentId = projectAgentRow?.agente_id || null;
      }
      setProjectAgentId(resolvedAgentId);

      if (projectCompanyId) {
        setLoadingConnections(true);
        const { data: connectionsData, error: connectionsError } = await supabase
          .from('connections')
          .select('id, name, number, status, type, agente_id, ai_id, with_ai')
          .eq('company_id', projectCompanyId)
          .eq('type', 'whatsapp')
          .order('name');

        if (connectionsError) {
          console.error('Erro ao carregar conexões disponíveis:', connectionsError);
          setAvailableConnections([]);
        } else {
          const mappedConnections = (connectionsData || []).map((conn) => {
            const isCurrent = conn.id === currentConnectionId;
            const linkedToOtherAgent = (
              (!!conn.agente_id && conn.agente_id !== resolvedAgentId) ||
              (!!conn.ai_id && conn.ai_id !== resolvedAgentId)
            ) && !isCurrent;

            return {
              ...conn,
              isCurrent,
              linkedToOtherAgent,
              statusLabel: conn.status || 'Desconhecido'
            };
          });
          setAvailableConnections(mappedConnections);
        }
        setLoadingConnections(false);
      }

      // Buscar conexão vinculada ao projeto
      const connectionId = project?.settings?.gas_distributor?.connection_id;
      if (connectionId) {
        const { data: conn, error: connError } = await supabase
          .from('connections')
          .select('id, name, number, status')
          .eq('id', connectionId)
          .single();

        if (!connError && conn) {
          setConnection(conn);
        }
      } else {
        setConnection(null);
      }

      // Buscar grupo já configurado
      const groupId = project?.settings?.gas_distributor?.group_id;
      const groupName = project?.settings?.gas_distributor?.group_name;
      if (groupId) {
        setSelectedGroup({ jid: groupId, name: groupName || 'Grupo configurado' });
      } else {
        setSelectedGroup(null);
      }

      // Buscar tags configuradas
      const tags = project?.settings?.gas_distributor?.tags_configuradas || [];
      setTagsConfiguradas(tags);

      // Buscar configuração do formulário
      const formConfigData = project?.settings?.gas_distributor?.form_config || {
        campos_obrigatorios_cliente: {
          nome: true,
          telefone: true,
          endereco: false,
          numero: false,
          bairro: false,
          cidade: false,
          complemento: false,
          cep: false,
          forma_pagamento: false
        },
        campos_obrigatorios_endereco: {
          endereco: true,
          numero: true,
          bairro: true,
          cidade: true,
          complemento: false,
          cep: false
        }
      };
      setFormConfig(formConfigData);

      // Buscar configurações do gas_distributor_config (incluindo dados da empresa)
      const { data: gasConfig, error: gasConfigError } = await supabase
        .from('gas_distributor_config')
        .select('politica_entrega, politica_cancelamento, politica_vendas, horarios_funcionamento, formas_pagamento, restricoes_agente, nome_fantasia, descricao, endereco_base, telefone_empresa, email_contato, website, areas_cobertura, instrucoes_atendimento')
        .eq('project_id', project.id)
        .maybeSingle();

      if (!gasConfigError && gasConfig) {
        // Carregar dados da empresa
        setEmpresaData({
          nomeFantasia: gasConfig.nome_fantasia || project?.name || '',
          descricao: gasConfig.descricao || project?.description || '',
          enderecoBase: gasConfig.endereco_base || {
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            cidade: '',
            estado: '',
            cep: ''
          },
          telefoneEmpresa: gasConfig.telefone_empresa || '',
          emailContato: gasConfig.email_contato || '',
          website: gasConfig.website || ''
        });

        // Carregar políticas
        setPoliticas({
          politica_entrega: gasConfig.politica_entrega || '',
          politica_cancelamento: gasConfig.politica_cancelamento || '',
          politica_vendas: gasConfig.politica_vendas || ''
        });

        // Carregar horários de funcionamento
        if (gasConfig.horarios_funcionamento) {
          setHorariosFuncionamento(gasConfig.horarios_funcionamento);
        }

        // Carregar formas de pagamento
        if (gasConfig.formas_pagamento && Array.isArray(gasConfig.formas_pagamento)) {
          // Verificar estrutura dos dados
          const primeiroItem = gasConfig.formas_pagamento[0];
          
          if (typeof primeiroItem === 'string') {
            // Estrutura antiga: array de strings ['dinheiro', 'pix']
            const formasConvertidas = [
              { tipo: 'dinheiro', ativo: gasConfig.formas_pagamento.includes('dinheiro'), observacoes: '', instrucoes: '' },
              { tipo: 'pix', ativo: gasConfig.formas_pagamento.includes('pix'), tipoPix: 'chave', chavePix: '', observacoes: '', instrucoes: '' },
              { tipo: 'cartao', ativo: gasConfig.formas_pagamento.includes('cartao'), observacoes: '', instrucoes: '' }
            ];
            setFormasPagamento(formasConvertidas);
          } else if (primeiroItem && typeof primeiroItem === 'object') {
            // Estrutura de objeto: verificar se tem campo 'ativo'
            if ('ativo' in primeiroItem) {
              // Estrutura nova completa: {tipo, ativo, observacoes, instrucoes, tipoPix, chavePix}
              // Garantir que campos PIX existam
              const formasCompletas = gasConfig.formas_pagamento.map(f => ({
                ...f,
                tipoPix: f.tipoPix || 'chave',
                chavePix: f.chavePix || ''
              }));
              setFormasPagamento(formasCompletas);
            } else {
              // Estrutura parcial: {tipo} apenas - converter para estrutura completa
              const formasCompletas = gasConfig.formas_pagamento.map(f => ({
                tipo: f.tipo,
                ativo: true, // Se está no array, está ativo
                tipoPix: f.tipoPix || 'chave',
                chavePix: f.chavePix || '',
                observacoes: f.observacoes || '',
                instrucoes: f.instrucoes || ''
              }));
              
              // Garantir que todas as 3 formas existam
              const todasFormas = [
                formasCompletas.find(f => f.tipo === 'dinheiro') || { tipo: 'dinheiro', ativo: false, observacoes: '', instrucoes: '' },
                formasCompletas.find(f => f.tipo === 'pix') || { tipo: 'pix', ativo: false, tipoPix: 'chave', chavePix: '', observacoes: '', instrucoes: '' },
                formasCompletas.find(f => f.tipo === 'cartao') || { tipo: 'cartao', ativo: false, observacoes: '', instrucoes: '' }
              ];
              
              setFormasPagamento(todasFormas);
            }
          }
        }

        // Carregar restrições do agente
        if (gasConfig.restricoes_agente && Array.isArray(gasConfig.restricoes_agente)) {
          setRestricoesAgente(gasConfig.restricoes_agente);
        } else {
          // Se não existir, inicializar com array vazio
          setRestricoesAgente([]);
        }

        // Carregar áreas de cobertura
        if (gasConfig.areas_cobertura && Array.isArray(gasConfig.areas_cobertura)) {
          setAreasCobertura(gasConfig.areas_cobertura);
        } else {
          setAreasCobertura([]);
        }

        // Carregar instruções de atendimento
        if (gasConfig.instrucoes_atendimento && typeof gasConfig.instrucoes_atendimento === 'object') {
          setInstrucoesAtendimento({
            promocoes_combos: gasConfig.instrucoes_atendimento.promocoes_combos || { ativo: false, texto: '' },
            atendimento_inicial: gasConfig.instrucoes_atendimento.atendimento_inicial || { ativo: false, texto: '' },
            instrucoes_gerais: gasConfig.instrucoes_atendimento.instrucoes_gerais || { ativo: false, texto: '' },
            pos_venda: gasConfig.instrucoes_atendimento.pos_venda || { ativo: false, texto: '' },
            programa_indicacao: gasConfig.instrucoes_atendimento.programa_indicacao || { ativo: false, texto: '' },
            negociacao_precos: gasConfig.instrucoes_atendimento.negociacao_precos || { ativo: false, texto: '' }
          });
        }
      }

    } catch (error) {
      console.error('Erro ao carregar dados do projeto:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    setEditingTag(null);
    setTagForm({
      nome: '',
      descricao: '',
      regra_aplicacao: '',
      desconto_percentual: '',
      cor: '#3B82F6',
      icone: 'label',
      ativa: true
    });
    setShowTagForm(true);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setTagForm({
      nome: tag.nome,
      descricao: tag.descricao,
      regra_aplicacao: tag.regra_aplicacao,
      desconto_percentual: tag.desconto_percentual || '',
      cor: tag.cor,
      icone: tag.icone,
      ativa: tag.ativa
    });
    setShowTagForm(true);
  };

  const handleSaveTag = () => {
    if (!tagForm.nome.trim()) {
      alert('Nome da tag é obrigatório');
      return;
    }
    if (!tagForm.descricao.trim()) {
      alert('Descrição da tag é obrigatória');
      return;
    }
    if (!tagForm.regra_aplicacao.trim()) {
      alert('Regra de aplicação é obrigatória');
      return;
    }

    if (editingTag) {
      // Editar tag existente
      setTagsConfiguradas(tagsConfiguradas.map(tag => 
        tag.id === editingTag.id 
          ? { ...tag, ...tagForm }
          : tag
      ));
    } else {
      // Adicionar nova tag
      const newTag = {
        id: crypto.randomUUID(),
        ...tagForm,
        created_at: new Date().toISOString()
      };
      setTagsConfiguradas([...tagsConfiguradas, newTag]);
    }

    setShowTagForm(false);
    setEditingTag(null);
  };

  const handleDeleteTag = (tagId) => {
    if (confirm('Tem certeza que deseja excluir esta tag?')) {
      setTagsConfiguradas(tagsConfiguradas.filter(tag => tag.id !== tagId));
    }
  };

  const handleToggleTagAtiva = (tagId) => {
    setTagsConfiguradas(tagsConfiguradas.map(tag =>
      tag.id === tagId ? { ...tag, ativa: !tag.ativa } : tag
    ));
  };

  const loadGroups = async () => {
    if (!connection?.id) {
      alert('Nenhuma conexão vinculada ao projeto');
      return;
    }

    setLoadingGroups(true);
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      
      const response = await fetch(
        `${BACKEND_NODE_URL}/connections/groups/${connection.id}?force=true`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao carregar grupos');
      }

      const data = await response.json();
      setAvailableGroups(data.groups || []);

    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
      alert('Erro ao carregar grupos: ' + error.message);
      setAvailableGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
  };

  const handleSelectConnection = (conn) => {
    if (conn.linkedToOtherAgent) return;
    setConnection(conn);
    setSelectedGroup(null);
    setAvailableGroups([]);
  };

  const handleToggleFormField = (section, field) => {
    setFormConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field]
      }
    }));
  };

  // Handler para atualizar políticas
  const handlePoliticaChange = (field, value) => {
    setPoliticas(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handler para atualizar horários de funcionamento
  const handleHorarioChange = (dia, campo, valor) => {
    setHorariosFuncionamento(prev => ({
      ...prev,
      [dia]: {
        ...prev[dia],
        [campo]: valor
      }
    }));
  };

  // Handler para alternar dia ativo/inativo
  const handleToggleDia = (dia) => {
    setHorariosFuncionamento(prev => ({
      ...prev,
      [dia]: {
        ...prev[dia],
        ativo: !prev[dia].ativo
      }
    }));
  };

  // Handler para atualizar formas de pagamento
  const handleToggleFormaPagamento = (tipo) => {
    setFormasPagamento(prev => 
      prev.map(f => f.tipo === tipo ? { ...f, ativo: !f.ativo } : f)
    );
  };

  // Handler para atualizar observações/instruções
  const handleUpdateFormaPagamento = (tipo, campo, valor) => {
    setFormasPagamento(prev =>
      prev.map(f => f.tipo === tipo ? { ...f, [campo]: valor } : f)
    );
  };

  // Handler para adicionar forma de pagamento "outros"
  const handleAdicionarOutros = () => {
    setFormasPagamento(prev => [
      ...prev,
      { tipo: 'outros', ativo: true, nome: '', observacoes: '', instrucoes: '' }
    ]);
    setMostrarAdicionarOutros(false);
  };

  // Handler para remover forma de pagamento "outros"
  const handleRemoverFormaPagamento = (index) => {
    setFormasPagamento(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const updatedSettings = {
        ...(project.settings || {}),
        gas_distributor: {
          ...(project.settings?.gas_distributor || {}),
          connection_id: connection?.id || null,
          group_id: selectedGroup?.jid || null,
          group_name: selectedGroup?.name || null,
          tags_configuradas: tagsConfiguradas,
          form_config: formConfig
        }
      };

      // 1. Salvar na tabela projects (nome e descrição)
      const { error: projectError } = await supabase
        .from('projects')
        .update({
          name: empresaData.nomeFantasia || project.name,
          description: empresaData.descricao || project.description,
          settings: updatedSettings
        })
        .eq('id', project.id);

      if (projectError) throw projectError;

      const previousConnectionId = project?.settings?.gas_distributor?.connection_id || null;
      const nextConnectionId = connection?.id || null;
      const effectiveAgentId = projectAgentId || project?.ai_agent_id || null;

      // Sincronizar vínculo técnico agente <-> conexão para manter roteamento de mensagens consistente
      if (effectiveAgentId) {
        if (previousConnectionId && previousConnectionId !== nextConnectionId) {
          const { error: oldConnectionError } = await supabase
            .from('connections')
            .update({ with_ai: false, ai_id: null })
            .eq('id', previousConnectionId)
            .eq('ai_id', effectiveAgentId);

          if (oldConnectionError) {
            console.warn('Aviso ao limpar conexão anterior:', oldConnectionError.message);
          }
        }

        const { error: agentConnectionError } = await supabase
          .from('agentes')
          .update({ conexao_id: nextConnectionId })
          .eq('id', effectiveAgentId);

        if (agentConnectionError) {
          console.warn('Aviso ao atualizar agentes.conexao_id:', agentConnectionError.message);
        }

        if (nextConnectionId) {
          const { error: newConnectionError } = await supabase
            .from('connections')
            .update({ with_ai: true, ai_id: effectiveAgentId })
            .eq('id', nextConnectionId);

          if (newConnectionError) {
            console.warn('Aviso ao atualizar connections.ai_id/with_ai:', newConnectionError.message);
          }
        }
      }

      // 2. Salvar também na tabela gas_distributor_config (incluindo dados da empresa)
      const { error: configError } = await supabase
        .from('gas_distributor_config')
        .update({
          nome_fantasia: empresaData.nomeFantasia,
          descricao: empresaData.descricao,
          endereco_base: empresaData.enderecoBase,
          telefone_empresa: empresaData.telefoneEmpresa,
          email_contato: empresaData.emailContato,
          website: empresaData.website,
          grupo_whatsapp_id: selectedGroup?.jid || null,
          grupo_whatsapp_nome: selectedGroup?.name || '',
          politica_entrega: politicas.politica_entrega,
          politica_cancelamento: politicas.politica_cancelamento,
          politica_vendas: politicas.politica_vendas,
          horarios_funcionamento: horariosFuncionamento,
          formas_pagamento: formasPagamento,
          restricoes_agente: restricoesAgente,
          areas_cobertura: areasCobertura,
          instrucoes_atendimento: instrucoesAtendimento,
          updated_at: new Date().toISOString()
        })
        .eq('project_id', project.id);

      if (configError) {
        console.error('Erro ao salvar na gas_distributor_config:', configError);
        // Não lançar erro, pois já salvou em projects.settings
      } else {
        console.log('✅ Configurações salvas na gas_distributor_config');
      }

      alert('Projeto atualizado com sucesso!');
      onSave?.();
      onClose();

    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

  const normalizarBairro = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  const handleBuscarBairrosPorCidade = async () => {
    const cidade = cidadeBuscaBairros.trim();
    if (!cidade) {
      setErroBuscaBairros('Informe a cidade para buscar os bairros.');
      return;
    }

    try {
      setCarregandoBairros(true);
      setErroBuscaBairros('');
      setBairrosEncontrados([]);
      setBairrosSelecionados([]);

      const res = await fetch(`${BACKEND_NODE_URL}/api/gas-distributor/locations/osm-neighborhoods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          city: cidade,
          state: ufBuscaBairros,
          country: 'Brazil'
        })
      });

      const json = await res.json();
      if (!res.ok || !json?.success) {
        setErroBuscaBairros(json?.error || 'Não foi possível buscar bairros no momento.');
        return;
      }

      const bairros = Array.isArray(json.neighborhoods) ? json.neighborhoods : [];
      if (bairros.length === 0) {
        setErroBuscaBairros('Nenhum bairro encontrado para esta cidade no OSM.');
        return;
      }

      if (json?.resolvedCity) {
        setCidadeBuscaBairros(json.resolvedCity);
      }
      if (json?.resolvedStateCode && UFS.includes(json.resolvedStateCode)) {
        setUfBuscaBairros(json.resolvedStateCode);
      }

      setBairrosEncontrados(bairros);
      setBairrosSelecionados(bairros);
    } catch (error) {
      setErroBuscaBairros(error.message || 'Erro ao buscar bairros.');
    } finally {
      setCarregandoBairros(false);
    }
  };

  const handleToggleBairroSelecionado = (bairro) => {
    setBairrosSelecionados((prev) =>
      prev.includes(bairro)
        ? prev.filter((b) => b !== bairro)
        : [...prev, bairro]
    );
  };

  const handleAdicionarBairrosSelecionados = () => {
    const taxa = String(taxaPadraoBairros || '').trim();
    const tempo = String(tempoPadraoBairros || '').trim();

    if (bairrosSelecionados.length === 0) {
      setErroBuscaBairros('Selecione ao menos um bairro para adicionar.');
      return;
    }
    if (!taxa || !tempo) {
      setErroBuscaBairros('Informe taxa e tempo padrão para adicionar os bairros.');
      return;
    }

    const existentes = new Set((areasCobertura || []).map((a) => normalizarBairro(a?.bairro)));
    const novos = bairrosSelecionados
      .filter((bairro) => !existentes.has(normalizarBairro(bairro)))
      .map((bairro) => ({
        bairro,
        taxa,
        tempoEntrega: tempo,
        naoAtendemos: false
      }));

    if (novos.length === 0) {
      setErroBuscaBairros('Todos os bairros selecionados já existem na lista.');
      return;
    }

    setAreasCobertura((prev) => [...prev, ...novos]);
    setErroBuscaBairros('');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content gas-distributor-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <AppIcon name="edit" />
            Editar Projeto - Gas Distributor
          </h2>
          <button className="btn-close" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        {/* Navegação por abas */}
        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === 'geral' ? 'active' : ''}`}
            onClick={() => setActiveTab('geral')}
          >
            <AppIcon name="business" />
            Geral
          </button>
          <button
            className={`tab-button ${activeTab === 'conexoes' ? 'active' : ''}`}
            onClick={() => setActiveTab('conexoes')}
          >
            <AppIcon name="link" />
            Conexões
          </button>
          <button
            className={`tab-button ${activeTab === 'politicas' ? 'active' : ''}`}
            onClick={() => setActiveTab('politicas')}
          >
            <AppIcon name="gavel" />
            Políticas
          </button>
          <button
            className={`tab-button ${activeTab === 'horarios' ? 'active' : ''}`}
            onClick={() => setActiveTab('horarios')}
          >
            <AppIcon name="schedule" />
            Horários
          </button>
          <button
            className={`tab-button ${activeTab === 'pagamento' ? 'active' : ''}`}
            onClick={() => setActiveTab('pagamento')}
          >
            <AppIcon name="payments" />
            Pagamento
          </button>
          <button
            className={`tab-button ${activeTab === 'restricoes' ? 'active' : ''}`}
            onClick={() => setActiveTab('restricoes')}
          >
            <AppIcon name="block" />
            Restrições
          </button>
          <button
            className={`tab-button ${activeTab === 'tags' ? 'active' : ''}`}
            onClick={() => setActiveTab('tags')}
          >
            <AppIcon name="label" />
            Tags
          </button>
          <button
            className={`tab-button ${activeTab === 'formulario' ? 'active' : ''}`}
            onClick={() => setActiveTab('formulario')}
          >
            <AppIcon name="contact_info" />
            Informações do Cliente
          </button>
          <button
            className={`tab-button ${activeTab === 'areas' ? 'active' : ''}`}
            onClick={() => setActiveTab('areas')}
          >
            <AppIcon name="map" />
            Áreas
          </button>
          <button
            className={`tab-button ${activeTab === 'instrucoes' ? 'active' : ''}`}
            onClick={() => setActiveTab('instrucoes')}
          >
            <AppIcon name="support_agent" />
            Instruções
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-container">
              <AppIcon name="sync" className="spinning" />
              <p>Carregando dados...</p>
            </div>
          ) : (
            <>
              {/* ABA: Informações Gerais da Empresa */}
              {activeTab === 'geral' && (
                <>
                  {/* Seção: Dados da Empresa */}
                  <div className="config-section">
                    <h3>
                      <AppIcon name="business" />
                      Dados da Empresa
                    </h3>
                    <p className="section-description">
                      Informações básicas da empresa que serão exibidas no atendimento.
                    </p>

                    {/* Nome Fantasia */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="store" />
                        Nome Fantasia
                      </label>
                      <input
                        type="text"
                        placeholder="Nome da empresa"
                        value={empresaData.nomeFantasia}
                        onChange={(e) => setEmpresaData(prev => ({ ...prev, nomeFantasia: e.target.value }))}
                        className="input-step"
                      />
                    </div>

                    {/* Descrição */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="description" />
                        Descrição
                      </label>
                      <textarea
                        placeholder="Descrição da empresa (ex: Distribuidor de gás e água)"
                        value={empresaData.descricao}
                        onChange={(e) => setEmpresaData(prev => ({ ...prev, descricao: e.target.value }))}
                        rows={3}
                        className="textarea-step"
                      />
                    </div>

                    {/* Telefone */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="phone" />
                        Telefone da Empresa
                      </label>
                      <input
                        type="text"
                        placeholder="(XX) XXXXX-XXXX"
                        value={empresaData.telefoneEmpresa}
                        onChange={(e) => setEmpresaData(prev => ({ ...prev, telefoneEmpresa: e.target.value }))}
                        className="input-step"
                      />
                    </div>

                    {/* Email */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="email" />
                        Email de Contato
                      </label>
                      <input
                        type="email"
                        placeholder="contato@empresa.com"
                        value={empresaData.emailContato}
                        onChange={(e) => setEmpresaData(prev => ({ ...prev, emailContato: e.target.value }))}
                        className="input-step"
                      />
                    </div>

                    {/* Website */}
                    <div className="form-field-step">
                      <label>
                        <AppIcon name="language" />
                        Website
                      </label>
                      <input
                        type="text"
                        placeholder="www.empresa.com.br"
                        value={empresaData.website}
                        onChange={(e) => setEmpresaData(prev => ({ ...prev, website: e.target.value }))}
                        className="input-step"
                      />
                    </div>
                  </div>

                  {/* Seção: Endereço Base */}
                  <div className="config-section">
                    <h3>
                      <AppIcon name="location_on" />
                      Endereço Base
                    </h3>
                    <p className="section-description">
                      Endereço de origem para cálculo de entregas.
                    </p>

                    <div className="endereco-grid">
                      {/* CEP */}
                      <div className="form-field-step">
                        <label>CEP</label>
                        <input
                          type="text"
                          placeholder="XXXXX-XXX"
                          value={empresaData.enderecoBase.cep}
                          onChange={(e) => setEmpresaData(prev => ({
                            ...prev,
                            enderecoBase: { ...prev.enderecoBase, cep: e.target.value }
                          }))}
                          className="input-step"
                        />
                      </div>

                      {/* Logradouro */}
                      <div className="form-field-step full-width">
                        <label>Logradouro</label>
                        <input
                          type="text"
                          placeholder="Rua, Avenida, etc."
                          value={empresaData.enderecoBase.logradouro}
                          onChange={(e) => setEmpresaData(prev => ({
                            ...prev,
                            enderecoBase: { ...prev.enderecoBase, logradouro: e.target.value }
                          }))}
                          className="input-step"
                        />
                      </div>

                      {/* Número */}
                      <div className="form-field-step">
                        <label>Número</label>
                        <input
                          type="text"
                          placeholder="123"
                          value={empresaData.enderecoBase.numero}
                          onChange={(e) => setEmpresaData(prev => ({
                            ...prev,
                            enderecoBase: { ...prev.enderecoBase, numero: e.target.value }
                          }))}
                          className="input-step"
                        />
                      </div>

                      {/* Complemento */}
                      <div className="form-field-step">
                        <label>Complemento</label>
                        <input
                          type="text"
                          placeholder="Sala, Loja, etc."
                          value={empresaData.enderecoBase.complemento}
                          onChange={(e) => setEmpresaData(prev => ({
                            ...prev,
                            enderecoBase: { ...prev.enderecoBase, complemento: e.target.value }
                          }))}
                          className="input-step"
                        />
                      </div>

                      {/* Bairro */}
                      <div className="form-field-step">
                        <label>Bairro</label>
                        <input
                          type="text"
                          placeholder="Bairro"
                          value={empresaData.enderecoBase.bairro}
                          onChange={(e) => setEmpresaData(prev => ({
                            ...prev,
                            enderecoBase: { ...prev.enderecoBase, bairro: e.target.value }
                          }))}
                          className="input-step"
                        />
                      </div>

                      {/* Cidade */}
                      <div className="form-field-step">
                        <label>Cidade</label>
                        <input
                          type="text"
                          placeholder="Cidade"
                          value={empresaData.enderecoBase.cidade}
                          onChange={(e) => setEmpresaData(prev => ({
                            ...prev,
                            enderecoBase: { ...prev.enderecoBase, cidade: e.target.value }
                          }))}
                          className="input-step"
                        />
                      </div>

                      {/* Estado */}
                      <div className="form-field-step">
                        <label>Estado</label>
                        <input
                          type="text"
                          placeholder="UF"
                          value={empresaData.enderecoBase.estado}
                          onChange={(e) => setEmpresaData(prev => ({
                            ...prev,
                            enderecoBase: { ...prev.enderecoBase, estado: e.target.value }
                          }))}
                          className="input-step"
                          maxLength={2}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ABA: Conexões */}
              {activeTab === 'conexoes' && (
                <>
                  {/* Seção: Conexão Vinculada */}
                  <div className="config-section">
                    <h3>
                      <AppIcon name="link" />
                      Conexão Vinculada
                    </h3>
                    
                    {connection ? (
                      <div className="connection-card">
                        <div className="connection-status">
                          <span className={`status-indicator ${connection.status?.toLowerCase() === 'conectado' ? 'connected' : 'disconnected'}`}></span>
                          <span className="status-text">{connection.status || 'Desconhecido'}</span>
                        </div>
                        <div className="connection-info">
                          <AppIcon name="smartphone" />
                          <div className="connection-details">
                            <p className="connection-name">{connection.name}</p>
                            {connection.number && (
                              <p className="connection-number">{connection.number}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="no-connection">
                        <AppIcon name="link_off" />
                        <p>Nenhuma conexão vinculada ao projeto</p>
                      </div>
                    )}

                    <div className="available-groups" style={{ marginTop: '16px' }}>
                      <p className="section-label">Conexões disponíveis:</p>
                      {loadingConnections ? (
                        <div className="no-groups">
                          <AppIcon name="sync" className="spinning" />
                          <p>Carregando conexões...</p>
                        </div>
                      ) : availableConnections.length === 0 ? (
                        <div className="no-groups">
                          <AppIcon name="info" />
                          <p>Nenhuma conexão WhatsApp disponível para esta empresa</p>
                        </div>
                      ) : (
                        <div className="groups-list">
                          {availableConnections.map((conn) => (
                            <div
                              key={conn.id}
                              className={`group-card ${connection?.id === conn.id ? 'selected' : ''}`}
                              onClick={() => handleSelectConnection(conn)}
                              style={{
                                opacity: conn.linkedToOtherAgent ? 0.6 : 1,
                                cursor: conn.linkedToOtherAgent ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <AppIcon name="smartphone" />
                              <div className="group-info">
                                <span className="group-name">{conn.name}</span>
                                <span className="group-participants">{conn.number || 'Número não disponível'}</span>
                                <span className="group-participants">
                                  Status: {conn.statusLabel}
                                  {conn.linkedToOtherAgent ? ' • Vinculada a outro agente' : ''}
                                </span>
                              </div>
                              {connection?.id === conn.id && (
                                <AppIcon name="check_circle" className="check-icon" />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Seção: Grupo WhatsApp */}
                  <div className="config-section">
                    <h3>
                      <AppIcon name="groups" />
                      Grupo WhatsApp
                    </h3>

                    <div className="group-config">
                      <button
                        className="btn-load-groups"
                        onClick={loadGroups}
                        disabled={loadingGroups || !connection}
                      >
                        {loadingGroups ? (
                          <>
                            <AppIcon name="sync" className="spinning" />
                            Carregando grupos...
                          </>
                        ) : (
                          <>
                            <AppIcon name="refresh" />
                            Carregar Grupos
                          </>
                        )}
                      </button>

                      {/* Grupo selecionado */}
                      {selectedGroup && (
                        <div className="selected-group">
                          <p className="section-label">Grupo selecionado:</p>
                          <div className="group-card selected">
                            <AppIcon name="group" />
                            <span className="group-name">{selectedGroup.name}</span>
                            <button
                              className="btn-remove-group"
                              onClick={() => setSelectedGroup(null)}
                            >
                              <AppIcon name="close" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Lista de grupos disponíveis */}
                      {availableGroups.length > 0 && (
                        <div className="available-groups">
                          <p className="section-label">Grupos disponíveis:</p>
                          <div className="groups-list">
                            {availableGroups.map((group) => (
                              <div
                                key={group.jid}
                                className={`group-card ${selectedGroup?.jid === group.jid ? 'selected' : ''}`}
                                onClick={() => handleSelectGroup(group)}
                              >
                                <AppIcon name="group" />
                                <div className="group-info">
                                  <span className="group-name">{group.name}</span>
                                  {group.participant_count > 0 && (
                                    <span className="group-participants">
                                      {group.participant_count} participantes
                                    </span>
                                  )}
                                </div>
                                {selectedGroup?.jid === group.jid && (
                                  <AppIcon name="check_circle" className="check-icon" />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Mensagem quando não há grupos */}
                      {availableGroups.length === 0 && !loadingGroups && connection && (
                        <div className="no-groups">
                          <AppIcon name="info" />
                          <p>Clique em "Carregar Grupos" para ver os grupos disponíveis</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ABA: Políticas */}
              {activeTab === 'politicas' && (
                <div className="config-section">
                  <h3>
                    <AppIcon name="gavel" />
                    Políticas da Empresa
                  </h3>
                  <p className="section-description">
                    Configure as políticas de entrega, cancelamento e vendas que serão seguidas pelo agente de atendimento.
                  </p>

                  {/* Política de Entrega */}
                  <div className="form-field-step">
                    <label>
                      <AppIcon name="local_shipping" />
                      Política de Entrega
                    </label>
                    <textarea
                      placeholder="Ex: Entrega em até 45 minutos para pedidos imediatos. Entregas agendadas com no mínimo 2 horas de antecedência..."
                      value={politicas.politica_entrega}
                      onChange={(e) => handlePoliticaChange('politica_entrega', e.target.value)}
                      rows={4}
                      className="textarea-step"
                    />
                    <small>Informações sobre prazos, condições e procedimentos de entrega</small>
                  </div>

                  {/* Política de Cancelamento */}
                  <div className="form-field-step">
                    <label>
                      <AppIcon name="cancel" />
                      Política de Cancelamento
                    </label>
                    <textarea
                      placeholder="Ex: Cancelamento permitido até 30 minutos após confirmação do pedido..."
                      value={politicas.politica_cancelamento}
                      onChange={(e) => handlePoliticaChange('politica_cancelamento', e.target.value)}
                      rows={4}
                      className="textarea-step"
                    />
                    <small>Regras para cancelamento de pedidos e reembolsos</small>
                  </div>

                  {/* Política de Vendas e Valores */}
                  <div className="form-field-step">
                    <label>
                      <AppIcon name="payments" />
                      Política de Vendas e Valores
                    </label>
                    <textarea
                      placeholder="Ex: Pedido mínimo de R$ 50,00. Desconto de 5% para pagamento PIX. Promoção: leve 2 e pague 1 na primeira compra..."
                      value={politicas.politica_vendas}
                      onChange={(e) => handlePoliticaChange('politica_vendas', e.target.value)}
                      rows={4}
                      className="textarea-step"
                    />
                    <small>Condições comerciais, descontos, valores mínimos e promoções</small>
                  </div>
                </div>
              )}

              {/* ABA: Horários */}
              {activeTab === 'horarios' && (
                <div className="config-section">
                  <h3>
                    <AppIcon name="schedule" />
                    Horários de Funcionamento
                  </h3>
                  <p className="section-description">
                    Configure os horários de funcionamento da empresa. O agente usará essas informações para determinar se está aberto ou fechado.
                  </p>

                  <div className="horarios-grid">
                    {Object.entries(horariosFuncionamento).map(([dia, config]) => (
                      <div key={dia} className={`horario-row ${config.ativo ? '' : 'inactive'}`}>
                        <div className="dia-header">
                          <label className="dia-toggle">
                            <input
                              type="checkbox"
                              checked={config.ativo}
                              onChange={() => handleToggleDia(dia)}
                            />
                            <span className="dia-nome">
                              {dia.charAt(0).toUpperCase() + dia.slice(1)}
                            </span>
                          </label>
                        </div>
                        
                        {config.ativo && (
                          <div className="horario-inputs">
                            <div className="horario-field">
                              <label>Abertura</label>
                              <input
                                type="time"
                                value={config.abertura}
                                onChange={(e) => handleHorarioChange(dia, 'abertura', e.target.value)}
                              />
                            </div>
                            <span className="horario-separator">às</span>
                            <div className="horario-field">
                              <label>Fechamento</label>
                              <input
                                type="time"
                                value={config.fechamento}
                                onChange={(e) => handleHorarioChange(dia, 'fechamento', e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                        
                        {!config.ativo && (
                          <span className="fechado-label">Fechado</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ABA: Formas de Pagamento */}
              {activeTab === 'pagamento' && (
                <div className="config-section">
                  <h3>
                    <AppIcon name="payments" />
                    Formas de Pagamento
                  </h3>
                  <p className="section-description">
                    Configure as formas de pagamento aceitas e adicione informações importantes que o agente deve saber.
                  </p>

                  <div className="formas-pagamento-list">
                    {formasPagamento.map((forma, index) => (
                      <div key={`${forma.tipo}-${index}`} className={`forma-pagamento-item ${forma.ativo ? 'active' : 'inactive'}`}>
                        {/* Header com toggle */}
                        <div 
                          className="forma-pagamento-header"
                          onClick={() => handleToggleFormaPagamento(forma.tipo)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="forma-info">
                            <div className="forma-icon">
                              {forma.tipo === 'dinheiro' && <AppIcon name="payments" />}
                              {forma.tipo === 'pix' && <AppIcon name="qr_code" />}
                              {forma.tipo === 'cartao' && <AppIcon name="credit_card" />}
                              {forma.tipo === 'outros' && <AppIcon name="more_horiz" />}
                            </div>
                            <div className="forma-details">
                              <span className="forma-nome">
                                {forma.tipo === 'dinheiro' && 'Dinheiro'}
                                {forma.tipo === 'pix' && 'PIX'}
                                {forma.tipo === 'cartao' && 'Cartão de Crédito/Débito'}
                                {forma.tipo === 'outros' && (forma.nome || 'Outros')}
                              </span>
                              <span className={`forma-status ${forma.ativo ? 'active' : 'inactive'}`}>
                                {forma.ativo ? 'Aceito' : 'Não aceito'}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {forma.tipo === 'outros' && (
                              <button
                                className="btn-remove-forma"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoverFormaPagamento(index);
                                }}
                                title="Remover forma de pagamento"
                              >
                                <AppIcon name="delete" />
                              </button>
                            )}
                            <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={forma.ativo}
                                onChange={() => handleToggleFormaPagamento(forma.tipo)}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                        </div>

                        {/* Campos de informações extras (apenas se ativo) */}
                        {forma.ativo && (
                          <div className="forma-pagamento-body">
                            {/* Campo nome (apenas para "outros") */}
                            {forma.tipo === 'outros' && (
                              <div className="forma-field">
                                <label>
                                  <AppIcon name="edit" />
                                  Nome da Forma de Pagamento
                                </label>
                                <input
                                  type="text"
                                  placeholder="Ex: Transferência bancária, Boleto, Cheque"
                                  value={forma.nome || ''}
                                  onChange={(e) => handleUpdateFormaPagamento(forma.tipo, 'nome', e.target.value)}
                                  className="forma-input"
                                />
                                <small>Nome que aparecerá para o cliente</small>
                              </div>
                            )}

                            {/* Campos específicos para PIX */}
                            {forma.tipo === 'pix' && (
                              <>
                                <div className="forma-field">
                                  <label>
                                    <AppIcon name="category" />
                                    Tipo da Chave PIX
                                  </label>
                                  <select
                                    value={forma.tipoPix || 'chave'}
                                    onChange={(e) => handleUpdateFormaPagamento(forma.tipo, 'tipoPix', e.target.value)}
                                    className="forma-select"
                                  >
                                    <option value="chave">Chave Aleatória</option>
                                    <option value="telefone">Telefone</option>
                                    <option value="email">E-mail</option>
                                    <option value="cpf">CPF/CNPJ</option>
                                  </select>
                                  <small>Selecione o tipo da sua chave PIX</small>
                                </div>

                                <div className="forma-field">
                                  <label>
                                    <AppIcon name="key" />
                                    Chave PIX
                                  </label>
                                  <input
                                    type="text"
                                    placeholder={
                                      forma.tipoPix === 'telefone' ? 'Ex: (11) 99999-9999' :
                                      forma.tipoPix === 'email' ? 'Ex: pagamento@empresa.com' :
                                      forma.tipoPix === 'cpf' ? 'Ex: 000.000.000-00' :
                                      'Ex: Chave aleatória gerada pelo banco'
                                    }
                                    value={forma.chavePix || ''}
                                    onChange={(e) => handleUpdateFormaPagamento(forma.tipo, 'chavePix', e.target.value)}
                                    className="forma-input"
                                  />
                                  <small>Digite sua chave PIX para o agente enviar ao cliente</small>
                                </div>
                              </>
                            )}

                            <div className="forma-field">
                              <label>
                                <AppIcon name="info" />
                                Observações
                              </label>
                              <input
                                type="text"
                                placeholder={
                                  forma.tipo === 'pix' ? 'Ex: Pagamento deve ser feito antes da entrega' :
                                  forma.tipo === 'dinheiro' ? 'Ex: Precisa de troco? Informe o valor da nota' :
                                  forma.tipo === 'cartao' ? 'Ex: Aceitamos Visa, Mastercard, Elo' :
                                  'Ex: Dados bancários, instruções específicas'
                                }
                                value={forma.observacoes}
                                onChange={(e) => handleUpdateFormaPagamento(forma.tipo, 'observacoes', e.target.value)}
                                className="forma-input"
                              />
                              <small>Informações importantes sobre esta forma de pagamento</small>
                            </div>

                            <div className="forma-field">
                              <label>
                                <AppIcon name="description" />
                                Instruções para o Cliente
                              </label>
                              <textarea
                                placeholder={
                                  forma.tipo === 'pix' ? 'Ex: Após realizar o PIX, envie o comprovante para confirmar o pagamento' :
                                  forma.tipo === 'dinheiro' ? 'Ex: Pagamento na entrega. Tenha o valor exato ou informe se precisa de troco' :
                                  forma.tipo === 'cartao' ? 'Ex: Pagamento na entrega com maquininha. Aceitamos débito e crédito em até 3x' :
                                  'Ex: Como proceder com esta forma de pagamento'
                                }
                                value={forma.instrucoes}
                                onChange={(e) => handleUpdateFormaPagamento(forma.tipo, 'instrucoes', e.target.value)}
                                rows={3}
                                className="forma-textarea"
                              />
                              <small>Instruções que o agente deve passar ao cliente sobre esta forma de pagamento</small>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Botão para adicionar "Outros" */}
                    {!formasPagamento.some(f => f.tipo === 'outros') && (
                      <button
                        className="btn-add-forma-pagamento"
                        onClick={handleAdicionarOutros}
                      >
                        <AppIcon name="add" />
                        Adicionar Outra Forma de Pagamento
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ABA: Restrições */}
              {activeTab === 'restricoes' && (
                <div className="config-section">
                  <div className="section-header-with-action">
                    <h3>
                      <AppIcon name="block" />
                      Restrições do Agente
                    </h3>
                    <button 
                      className="btn-add-tag"
                      onClick={() => {
                        setNovaRestricao({ titulo: '', descricao: '', ativo: true });
                        setEditandoRestricao(null);
                        setModalRestricaoAberto(true);
                      }}
                    >
                      <AppIcon name="add" />
                      Nova Restrição
                    </button>
                  </div>
                  
                  <p className="section-description">
                    Configure restrições (o que o agente NUNCA/NÃO pode fazer). Exemplo: "NUNCA envie PIX diferente do cadastrado".
                  </p>

                  {/* Lista de restrições */}
                  <div className="restricoes-list">
                    {restricoesAgente.length === 0 ? (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '40px', 
                        color: '#64748b',
                        background: 'rgba(49, 117, 177, 0.05)',
                        borderRadius: '8px',
                        border: '1px dashed rgba(49, 117, 177, 0.3)'
                      }}>
                        <AppIcon name="block" style={{ fontSize: '48px', marginBottom: '16px', display: 'block' }} />
                        <p style={{ fontSize: '0.875rem', marginBottom: '8px' }}>Nenhuma restrição configurada</p>
                        <p style={{ fontSize: '0.75rem' }}>
                          Adicione restrições para controlar o que o agente NÃO pode fazer.
                        </p>
                      </div>
                    ) : (
                      restricoesAgente.map((restricao, index) => (
                        <div 
                          key={restricao.id || index} 
                          className={`restricao-item ${restricao.ativo ? 'active' : 'inactive'}`}
                          style={{
                            background: restricao.ativo ? 'rgba(49, 117, 177, 0.1)' : 'rgba(100, 116, 139, 0.05)',
                            border: `1px solid ${restricao.ativo ? 'rgba(49, 117, 177, 0.3)' : 'rgba(100, 116, 139, 0.2)'}`,
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                            opacity: restricao.ativo ? 1 : 0.6
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <h4 style={{ 
                                fontSize: '0.875rem', 
                                fontWeight: '600', 
                                color: restricao.ativo ? '#fff' : '#94a3b8',
                                marginBottom: '8px'
                              }}>
                                {restricao.titulo || 'Sem título'}
                              </h4>
                              <p style={{ 
                                fontSize: '0.8rem', 
                                color: '#94a3b8',
                                lineHeight: '1.5'
                              }}>
                                {restricao.descricao}
                              </p>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: '16px' }}>
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={restricao.ativo}
                                  onChange={() => {
                                    setRestricoesAgente(prev =>
                                      prev.map(r => r.id === restricao.id ? { ...r, ativo: !r.ativo } : r)
                                    );
                                  }}
                                />
                                <span className="toggle-slider"></span>
                              </label>
                              <button
                                onClick={() => {
                                  setEditandoRestricao(restricao);
                                  setNovaRestricao({ ...restricao });
                                  setModalRestricaoAberto(true);
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#94a3b8',
                                  cursor: 'pointer',
                                  padding: '4px'
                                }}
                              >
                                <AppIcon name="edit" style={{ fontSize: '18px' }} />
                              </button>
                              <button
                                onClick={() => {
                                  setRestricoesAgente(prev => prev.filter(r => r.id !== restricao.id));
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  padding: '4px'
                                }}
                              >
                                <AppIcon name="delete" style={{ fontSize: '18px' }} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div style={{ 
                    marginTop: '20px', 
                    padding: '16px', 
                    background: 'rgba(68, 225, 255, 0.05)',
                    border: '1px solid rgba(68, 225, 255, 0.2)',
                    borderRadius: '8px'
                  }}>
                    <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>
                      💡 <strong>Restrições</strong> são ordens negativas (NUNCA/NÃO faça X). Elas são injetadas na seção "🚫 RESTRIÇÕES DO PROJETO" do prompt.
                    </p>
                  </div>
                </div>
              )}

              {/* Modal de Restrição */}
              {modalRestricaoAberto && (
                <div className="modal-overlay" onClick={() => setModalRestricaoAberto(false)}>
                  <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '500px' }}>
                    <div className="modal-header">
                      <h3>{editandoRestricao ? 'Editar Restrição' : 'Nova Restrição'}</h3>
                      <button onClick={() => setModalRestricaoAberto(false)} className="close-button">
                        <AppIcon name="close" />
                      </button>
                    </div>
                    <div className="modal-body">
                      <div className="form-group">
                        <label>Título</label>
                        <input
                          type="text"
                          value={novaRestricao.titulo}
                          onChange={(e) => setNovaRestricao(prev => ({ ...prev, titulo: e.target.value }))}
                          placeholder="Ex: Nunca envie PIX diferente"
                        />
                      </div>
                      <div className="form-group">
                        <label>Descrição da Restrição</label>
                        <textarea
                          value={novaRestricao.descricao}
                          onChange={(e) => setNovaRestricao(prev => ({ ...prev, descricao: e.target.value }))}
                          placeholder="Ex: NUNCA envie o PIX que não seja o PIX cadastrado na empresa"
                          rows={3}
                        />
                      </div>
                      <div className="form-group">
                        <label className="toggle-label">
                          <input
                            type="checkbox"
                            checked={novaRestricao.ativo}
                            onChange={(e) => setNovaRestricao(prev => ({ ...prev, ativo: e.target.checked }))}
                          />
                          <span>Restrição ativa</span>
                        </label>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button onClick={() => setModalRestricaoAberto(false)} className="btn-cancel">
                        Cancelar
                      </button>
                      <button 
                        onClick={() => {
                          if (!novaRestricao.titulo || !novaRestricao.descricao) return;
                          
                          if (editandoRestricao) {
                            setRestricoesAgente(prev =>
                              prev.map(r => r.id === editandoRestricao.id ? { ...novaRestricao, id: r.id } : r)
                            );
                          } else {
                            const nova = {
                              ...novaRestricao,
                              id: `restricao_${Date.now()}`
                            };
                            setRestricoesAgente(prev => [...prev, nova]);
                          }
                          
                          setModalRestricaoAberto(false);
                          setEditandoRestricao(null);
                        }}
                        className="btn-save"
                      >
                        {editandoRestricao ? 'Salvar Alterações' : 'Adicionar Restrição'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA: Tags */}
              {activeTab === 'tags' && (
                <div className="config-section">
                  <div className="section-header-with-action">
                    <h3>
                      <AppIcon name="label" />
                      Tags Automáticas
                    </h3>
                    <button className="btn-add-tag" onClick={handleAddTag}>
                      <AppIcon name="add" />
                      Nova Tag
                    </button>
                  </div>

                  <p className="section-description">
                    Configure tags que serão aplicadas automaticamente aos clientes quando cadastrados, 
                    baseado em regras analisadas por IA.
                  </p>

                  {/* Lista de tags configuradas */}
                  {tagsConfiguradas.length > 0 ? (
                    <div className="tags-list">
                      {tagsConfiguradas.map((tag) => (
                        <div key={tag.id} className={`tag-card ${!tag.ativa ? 'inactive' : ''}`}>
                          <div className="tag-header">
                            <div className="tag-title-section">
                              <AppIcon name={tag.icone} className="tag-icon" style={{ color: tag.cor }} />
                              <div className="tag-info">
                                <h4 className="tag-name">{tag.nome}</h4>
                                <p className="tag-description">{tag.descricao}</p>
                              </div>
                            </div>
                            <div className="tag-actions">
                              <button
                                className={`btn-toggle-tag ${tag.ativa ? 'active' : 'inactive'}`}
                                onClick={() => handleToggleTagAtiva(tag.id)}
                                title={tag.ativa ? 'Desativar tag' : 'Ativar tag'}
                              >
                                <AppIcon name={tag.ativa ? 'toggle_on' : 'toggle_off'} />
                              </button>
                              <button
                                className="btn-edit-tag"
                                onClick={() => handleEditTag(tag)}
                                title="Editar tag"
                              >
                                <AppIcon name="edit" />
                              </button>
                              <button
                                className="btn-delete-tag"
                                onClick={() => handleDeleteTag(tag.id)}
                                title="Excluir tag"
                              >
                                <AppIcon name="delete" />
                              </button>
                            </div>
                          </div>
                          <div className="tag-rule">
                            <span className="rule-label">Regra:</span>
                            <p className="rule-text">{tag.regra_aplicacao}</p>
                          </div>
                          {tag.desconto_percentual && (
                            <div className="tag-discount">
                              <span className="discount-badge">
                                {tag.desconto_percentual}% de desconto
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-tags">
                      <AppIcon name="label_off" />
                      <p>Nenhuma tag configurada ainda</p>
                      <small>Clique em "Nova Tag" para adicionar a primeira tag</small>
                    </div>
                  )}

                {/* Formulário de criar/editar tag */}
                {showTagForm && (
                  <div className="tag-form-overlay" onClick={() => setShowTagForm(false)}>
                    <div className="tag-form-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="tag-form-header">
                        <h3>
                          <AppIcon name={editingTag ? 'edit' : 'add'} />
                          {editingTag ? 'Editar Tag' : 'Nova Tag'}
                        </h3>
                        <button 
                          className="btn-close-form" 
                          onClick={() => setShowTagForm(false)}
                        >
                          <AppIcon name="close" />
                        </button>
                      </div>

                      <div className="tag-form-body">
                        <div className="form-row">
                          <div className="form-field">
                            <label>Nome da Tag *</label>
                            <input
                              type="text"
                              value={tagForm.nome}
                              onChange={(e) => setTagForm({ ...tagForm, nome: e.target.value })}
                              placeholder="Ex: VIP, Zona Sul, Cliente Novo"
                              maxLength={50}
                            />
                            <small>{tagForm.nome.length}/50 caracteres</small>
                          </div>

                          <div className="form-field form-field-small">
                            <label>Cor</label>
                            <input
                              type="color"
                              value={tagForm.cor}
                              onChange={(e) => setTagForm({ ...tagForm, cor: e.target.value })}
                            />
                          </div>

                          <div className="form-field form-field-small">
                            <label>Ícone</label>
                            <select
                              value={tagForm.icone}
                              onChange={(e) => setTagForm({ ...tagForm, icone: e.target.value })}
                            >
                              <option value="label">label</option>
                              <option value="star">star</option>
                              <option value="location_on">location_on</option>
                              <option value="verified">verified</option>
                              <option value="workspace_premium">workspace_premium</option>
                              <option value="loyalty">loyalty</option>
                              <option value="local_offer">local_offer</option>
                              <option value="new_releases">new_releases</option>
                              <option value="favorite">favorite</option>
                              <option value="bookmark">bookmark</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-field">
                          <label>Descrição *</label>
                          <textarea
                            value={tagForm.descricao}
                            onChange={(e) => setTagForm({ ...tagForm, descricao: e.target.value })}
                            placeholder="Descreva o que esta tag representa e como diferencia o cliente"
                            rows={2}
                            maxLength={200}
                          />
                          <small>{tagForm.descricao.length}/200 caracteres</small>
                        </div>

                        <div className="form-field">
                          <label>Regra de Aplicação (Linguagem Natural) *</label>
                          <textarea
                            value={tagForm.regra_aplicacao}
                            onChange={(e) => setTagForm({ ...tagForm, regra_aplicacao: e.target.value })}
                            placeholder="Ex: Cliente mora em bairros nobres como Centro, Jardins, Alphaville ou região central da cidade"
                            rows={4}
                            maxLength={1000}
                          />
                          <small>{tagForm.regra_aplicacao.length}/1000 caracteres</small>
                          <p className="field-hint">
                            💡 Descreva em linguagem natural quando esta tag deve ser aplicada. 
                            A IA analisará os dados do cliente e aplicará a tag se a regra se enquadrar.
                          </p>
                        </div>

                        <div className="form-field">
                          <label>Desconto (%) - Opcional</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={tagForm.desconto_percentual}
                            onChange={(e) => setTagForm({ ...tagForm, desconto_percentual: e.target.value })}
                            placeholder="Ex: 10 para 10% de desconto"
                          />
                          <p className="field-hint">
                            💡 Se preenchido, este desconto será aplicado automaticamente para clientes com esta tag.
                          </p>
                        </div>

                        <div className="form-field-checkbox">
                          <label>
                            <input
                              type="checkbox"
                              checked={tagForm.ativa}
                              onChange={(e) => setTagForm({ ...tagForm, ativa: e.target.checked })}
                            />
                            <span>Tag ativa (será analisada quando selecionada)</span>
                          </label>
                        </div>
                      </div>

                      <div className="tag-form-footer">
                        <button 
                          className="btn-cancel-tag" 
                          onClick={() => setShowTagForm(false)}
                        >
                          Cancelar
                        </button>
                        <button 
                          className="btn-save-tag" 
                          onClick={handleSaveTag}
                        >
                          <AppIcon name="save" />
                          {editingTag ? 'Salvar Alterações' : 'Criar Tag'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* ABA: Informações do Cliente */}
              {activeTab === 'formulario' && (
                <div className="config-section">
                  <h3>
                    <AppIcon name="contact_info" />
                    Informações do Cliente
                  </h3>
                  <p className="section-description">
                    Configure quais campos de endereço serão coletados do cliente. Campos ativos serão solicitados, campos obrigatórios são exigidos.
                  </p>

                  {/* Campos do Endereço */}
                  <div className="form-config-section">
                    <h4>
                      <AppIcon name="location_on" />
                      Campos de Endereço
                    </h4>
                    <p className="subsection-description">
                      Configure os campos que serão coletados para endereço do cliente.
                    </p>
                    <div className="fields-grid">
                      {Object.entries(formConfig.campos_obrigatorios_endereco || {}).map(([field, required]) => (
                        <div key={field} className="field-toggle-card">
                          <div className="field-header">
                            <span className="field-name">
                              {field.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                            </span>
                            <span className={`field-status ${required ? 'required' : 'optional'}`}>
                              {required ? 'Obrigatório' : 'Opcional'}
                            </span>
                          </div>
                          <div className="field-controls">
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={true}
                                onChange={() => {}}
                              />
                              <span className="toggle-slider"></span>
                              <span className="toggle-text">Ativo</span>
                            </label>
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={required}
                                onChange={() => handleToggleFormField('campos_obrigatorios_endereco', field)}
                              />
                              <span>Obrigatório</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Campos do Cliente (Novo Cadastro) */}
                  <div className="form-config-section">
                    <h4>
                      <AppIcon name="person" />
                      Campos de Novo Cadastro
                    </h4>
                    <p className="subsection-description">
                      Configure os campos que serão coletados ao cadastrar um novo cliente.
                    </p>
                    <div className="fields-grid">
                      {Object.entries(formConfig.campos_obrigatorios_cliente || {}).map(([field, required]) => (
                        <div key={field} className="field-toggle-card">
                          <div className="field-header">
                            <span className="field-name">
                              {field.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                            </span>
                            <span className={`field-status ${required ? 'required' : 'optional'}`}>
                              {required ? 'Obrigatório' : 'Opcional'}
                            </span>
                          </div>
                          <div className="field-controls">
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={true}
                                onChange={() => {}}
                              />
                              <span className="toggle-slider"></span>
                              <span className="toggle-text">Ativo</span>
                            </label>
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={required}
                                onChange={() => handleToggleFormField('campos_obrigatorios_cliente', field)}
                              />
                              <span>Obrigatório</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-config-info">
                    <AppIcon name="info" />
                    <p>
                      <strong>Importante:</strong> Os campos marcados como obrigatórios serão exigidos pelo agente
                      antes de permitir o cadastro. Campos opcionais podem ser preenchidos posteriormente.
                    </p>
                  </div>
                </div>
              )}

              {/* ABA: Áreas de Cobertura */}
              {activeTab === 'areas' && (
                <div className="config-section">
                  <h3>
                    <AppIcon name="map" />
                    Áreas de Cobertura
                  </h3>
                  <p className="section-description">
                    Configure os bairros/regiões com taxa, tempo estimado e se o local é atendido ou bloqueado.
                  </p>

                  {/* Lista de áreas existentes */}
                  <div className="areas-list">
                    {areasCobertura.length > 0 ? (
                      areasCobertura.map((area, idx) => (
                        <div key={idx} className="area-item">
                          <div className="area-info">
                            <AppIcon name="location_on" />
                            <div className="area-details">
                              <span className="area-nome">{area.bairro}</span>
                              <div className="area-meta">
                                <span className="area-taxa">Taxa: R$ {area.taxa}</span>
                                <span className="area-tempo">Tempo: {area.tempoEntrega} min</span>
                                <span className="area-tempo">
                                  {area.naoAtendemos ? 'Status: Não atendemos' : 'Status: Atendemos'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            className="btn-remove-area"
                            onClick={() => setAreasCobertura(areasCobertura.filter((_, i) => i !== idx))}
                            title="Remover área"
                          >
                            <AppIcon name="delete" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="no-areas">
                        <AppIcon name="map" />
                        <p>Nenhuma área configurada</p>
                        <small>Adicione bairros/regiões para atendimento</small>
                      </div>
                    )}
                  </div>

                  {/* Formulário para adicionar nova área */}
                  <div className="add-area-form">
                    <h4>
                      <AppIcon name="travel_explore" />
                      Adicionar bairros por cidade (OSM)
                    </h4>

                    <div className="area-form-grid">
                      <div className="form-field-step">
                        <label>Cidade</label>
                        <input
                          type="text"
                          placeholder="Ex: Londrina"
                          value={cidadeBuscaBairros}
                          onChange={(e) => setCidadeBuscaBairros(e.target.value)}
                          className="input-step"
                        />
                      </div>
                      <div className="form-field-step">
                        <label>UF</label>
                        <select
                          value={ufBuscaBairros}
                          onChange={(e) => setUfBuscaBairros(e.target.value)}
                          className="input-step"
                        >
                          {UFS.map((uf) => (
                            <option key={uf} value={uf}>{uf}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        className="btn-add-area"
                        onClick={handleBuscarBairrosPorCidade}
                        disabled={!cidadeBuscaBairros.trim() || carregandoBairros}
                      >
                        <AppIcon name="search" />
                        {carregandoBairros ? 'Buscando...' : 'Buscar bairros'}
                      </button>
                    </div>

                    {erroBuscaBairros && (
                      <small style={{ color: '#ef4444' }}>{erroBuscaBairros}</small>
                    )}

                    {bairrosEncontrados.length > 0 && (
                      <>
                        <div className="area-form-grid" style={{ marginTop: '0.75rem' }}>
                          <div className="form-field-step">
                            <label>Taxa padrão (R$)</label>
                            <input
                              type="number"
                              placeholder="Ex: 25"
                              value={taxaPadraoBairros}
                              onChange={(e) => setTaxaPadraoBairros(e.target.value)}
                              className="input-step"
                            />
                          </div>
                          <div className="form-field-step">
                            <label>Tempo padrão (min)</label>
                            <input
                              type="number"
                              placeholder="Ex: 30"
                              value={tempoPadraoBairros}
                              onChange={(e) => setTempoPadraoBairros(e.target.value)}
                              className="input-step"
                            />
                          </div>
                          <button
                            className="btn-add-area"
                            onClick={handleAdicionarBairrosSelecionados}
                            disabled={!taxaPadraoBairros || !tempoPadraoBairros || bairrosSelecionados.length === 0}
                          >
                            <AppIcon name="playlist_add" />
                            Adicionar selecionados ({bairrosSelecionados.length})
                          </button>
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', marginBottom: '0.4rem' }}>
                          <button className="btn-add-area" onClick={() => setBairrosSelecionados(bairrosEncontrados)}>
                            Selecionar todos
                          </button>
                          <button className="btn-add-area" onClick={() => setBairrosSelecionados([])}>
                            Limpar seleção
                          </button>
                        </div>

                        <div className="areas-list" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                          {bairrosEncontrados.map((bairro) => (
                            <label key={bairro} className="area-item" style={{ cursor: 'pointer' }}>
                              <div className="area-info">
                                <input
                                  type="checkbox"
                                  checked={bairrosSelecionados.includes(bairro)}
                                  onChange={() => handleToggleBairroSelecionado(bairro)}
                                />
                                <div className="area-details">
                                  <span className="area-nome">{bairro}</span>
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="add-area-form">
                    <h4>
                      <AppIcon name="add_location" />
                      Adicionar Nova Área
                    </h4>
                    <div className="area-form-grid">
                      <div className="form-field-step">
                        <label>Bairro/Região</label>
                        <input
                          type="text"
                          placeholder="Ex: Centro, Zona Sul"
                          value={novaArea.bairro}
                          onChange={(e) => setNovaArea({ ...novaArea, bairro: e.target.value })}
                          className="input-step"
                        />
                      </div>
                      <div className="form-field-step">
                        <label>Taxa de Entrega (R$)</label>
                        <input
                          type="number"
                          placeholder="Ex: 25"
                          value={novaArea.taxa}
                          onChange={(e) => setNovaArea({ ...novaArea, taxa: e.target.value })}
                          className="input-step"
                        />
                      </div>
                      <div className="form-field-step">
                        <label>Tempo Estimado (min)</label>
                        <input
                          type="number"
                          placeholder="Ex: 30"
                          value={novaArea.tempoEntrega}
                          onChange={(e) => setNovaArea({ ...novaArea, tempoEntrega: e.target.value })}
                          className="input-step"
                        />
                      </div>
                      <div className="form-field-step">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                          <input
                            type="checkbox"
                            checked={novaArea.naoAtendemos}
                            onChange={(e) => setNovaArea({ ...novaArea, naoAtendemos: e.target.checked })}
                          />
                          Não atendemos este bairro
                        </label>
                        <small style={{ color: '#94a3b8' }}>
                          Marque para manter o bairro cadastrado, mas bloquear pedidos nessa região.
                        </small>
                      </div>
                      <button
                        className="btn-add-area"
                        onClick={() => {
                          if (novaArea.bairro && novaArea.taxa && novaArea.tempoEntrega) {
                            setAreasCobertura([...areasCobertura, { ...novaArea }]);
                            setNovaArea({ bairro: '', taxa: '', tempoEntrega: '', naoAtendemos: false });
                          }
                        }}
                        disabled={!novaArea.bairro || !novaArea.taxa || !novaArea.tempoEntrega}
                      >
                        <AppIcon name="add" />
                        Adicionar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA: Instruções de Atendimento */}
              {activeTab === 'instrucoes' && (
                <div className="config-section">
                  <h3>
                    <AppIcon name="support_agent" />
                    Instruções de Atendimento
                  </h3>
                  <p className="section-description">
                    Configure instruções específicas que o agente deve seguir durante o atendimento. 
                    Apenas instruções ativas serão enviadas ao prompt.
                  </p>

                  <div className="instrucoes-list">
                    {/* Promoções e Combos */}
                    <div className={`instrucao-item ${instrucoesAtendimento.promocoes_combos?.ativo ? 'active' : 'inactive'}`}>
                      <div 
                        className="instrucao-header"
                        onClick={() => setInstrucoesAtendimento(prev => ({
                          ...prev,
                          promocoes_combos: { ...prev.promocoes_combos, ativo: !prev.promocoes_combos?.ativo }
                        }))}
                      >
                        <div className="instrucao-title">
                          <AppIcon name="local_offer" />
                          <h4>Promoções e Combos</h4>
                        </div>
                        <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={instrucoesAtendimento.promocoes_combos?.ativo || false}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              promocoes_combos: { ...prev.promocoes_combos, ativo: e.target.checked }
                            }))}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      {instrucoesAtendimento.promocoes_combos?.ativo && (
                        <div className="instrucao-body">
                          <textarea
                            placeholder="Ex: Combo Gás + Água: água por R$20 (desconto R$5) quando comprada junto com gás. Desconto de R$10 ao comprar 2 botijões..."
                            value={instrucoesAtendimento.promocoes_combos?.texto || ''}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              promocoes_combos: { ...prev.promocoes_combos, texto: e.target.value }
                            }))}
                            rows={4}
                            className="textarea-step"
                          />
                          <small>Descreva promoções e combos disponíveis</small>
                        </div>
                      )}
                    </div>

                    {/* Atendimento Inicial */}
                    <div className={`instrucao-item ${instrucoesAtendimento.atendimento_inicial?.ativo ? 'active' : 'inactive'}`}>
                      <div 
                        className="instrucao-header"
                        onClick={() => setInstrucoesAtendimento(prev => ({
                          ...prev,
                          atendimento_inicial: { ...prev.atendimento_inicial, ativo: !prev.atendimento_inicial?.ativo }
                        }))}
                      >
                        <div className="instrucao-title">
                          <AppIcon name="waving_hand" />
                          <h4>Atendimento Inicial</h4>
                        </div>
                        <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={instrucoesAtendimento.atendimento_inicial?.ativo || false}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              atendimento_inicial: { ...prev.atendimento_inicial, ativo: e.target.checked }
                            }))}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      {instrucoesAtendimento.atendimento_inicial?.ativo && (
                        <div className="instrucao-body">
                          <textarea
                            placeholder="Ex: Saudação personalizada, perguntas iniciais, forma de apresentar a empresa..."
                            value={instrucoesAtendimento.atendimento_inicial?.texto || ''}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              atendimento_inicial: { ...prev.atendimento_inicial, texto: e.target.value }
                            }))}
                            rows={4}
                            className="textarea-step"
                          />
                          <small>Instruções para o início do atendimento</small>
                        </div>
                      )}
                    </div>

                    {/* Instruções Gerais */}
                    <div className={`instrucao-item ${instrucoesAtendimento.instrucoes_gerais?.ativo ? 'active' : 'inactive'}`}>
                      <div 
                        className="instrucao-header"
                        onClick={() => setInstrucoesAtendimento(prev => ({
                          ...prev,
                          instrucoes_gerais: { ...prev.instrucoes_gerais, ativo: !prev.instrucoes_gerais?.ativo }
                        }))}
                      >
                        <div className="instrucao-title">
                          <AppIcon name="description" />
                          <h4>Instruções Gerais</h4>
                        </div>
                        <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={instrucoesAtendimento.instrucoes_gerais?.ativo || false}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              instrucoes_gerais: { ...prev.instrucoes_gerais, ativo: e.target.checked }
                            }))}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      {instrucoesAtendimento.instrucoes_gerais?.ativo && (
                        <div className="instrucao-body">
                          <textarea
                            placeholder="Ex: Perguntar 'Precisa de troco?' para identificar forma de pagamento. Verificar se cliente já tem cadastro..."
                            value={instrucoesAtendimento.instrucoes_gerais?.texto || ''}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              instrucoes_gerais: { ...prev.instrucoes_gerais, texto: e.target.value }
                            }))}
                            rows={4}
                            className="textarea-step"
                          />
                          <small>Instruções gerais de atendimento</small>
                        </div>
                      )}
                    </div>

                    {/* Negociação de Preços */}
                    <div className={`instrucao-item ${instrucoesAtendimento.negociacao_precos?.ativo ? 'active' : 'inactive'}`}>
                      <div 
                        className="instrucao-header"
                        onClick={() => setInstrucoesAtendimento(prev => ({
                          ...prev,
                          negociacao_precos: { ...prev.negociacao_precos, ativo: !prev.negociacao_precos?.ativo }
                        }))}
                      >
                        <div className="instrucao-title">
                          <AppIcon name="price_check" />
                          <h4>Negociação de Preços</h4>
                        </div>
                        <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={instrucoesAtendimento.negociacao_precos?.ativo || false}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              negociacao_precos: { ...prev.negociacao_precos, ativo: e.target.checked }
                            }))}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      {instrucoesAtendimento.negociacao_precos?.ativo && (
                        <div className="instrucao-body">
                          <textarea
                            placeholder="Ex: Preço base R$125. Se cliente reclamar do preço, negociar até R$120 (primeira oferta) ou R$115 (mínimo). Apenas antes das 18h. Após 18h, sem desconto..."
                            value={instrucoesAtendimento.negociacao_precos?.texto || ''}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              negociacao_precos: { ...prev.negociacao_precos, texto: e.target.value }
                            }))}
                            rows={4}
                            className="textarea-step"
                          />
                          <small>Regras de negociação de preços</small>
                        </div>
                      )}
                    </div>

                    {/* Pós-Venda */}
                    <div className={`instrucao-item ${instrucoesAtendimento.pos_venda?.ativo ? 'active' : 'inactive'}`}>
                      <div 
                        className="instrucao-header"
                        onClick={() => setInstrucoesAtendimento(prev => ({
                          ...prev,
                          pos_venda: { ...prev.pos_venda, ativo: !prev.pos_venda?.ativo }
                        }))}
                      >
                        <div className="instrucao-title">
                          <AppIcon name="rate_review" />
                          <h4>Pós-Venda</h4>
                        </div>
                        <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={instrucoesAtendimento.pos_venda?.ativo || false}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              pos_venda: { ...prev.pos_venda, ativo: e.target.checked }
                            }))}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      {instrucoesAtendimento.pos_venda?.ativo && (
                        <div className="instrucao-body">
                          <textarea
                            placeholder="Ex: Após entrega confirmada, enviar mensagem de agradecimento e solicitar avaliação no Google. Oferecer voucher de R$15 para recompra em 30 dias..."
                            value={instrucoesAtendimento.pos_venda?.texto || ''}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              pos_venda: { ...prev.pos_venda, texto: e.target.value }
                            }))}
                            rows={4}
                            className="textarea-step"
                          />
                          <small>Instruções de pós-venda e fidelização</small>
                        </div>
                      )}
                    </div>

                    {/* Programa de Indicação */}
                    <div className={`instrucao-item ${instrucoesAtendimento.programa_indicacao?.ativo ? 'active' : 'inactive'}`}>
                      <div 
                        className="instrucao-header"
                        onClick={() => setInstrucoesAtendimento(prev => ({
                          ...prev,
                          programa_indicacao: { ...prev.programa_indicacao, ativo: !prev.programa_indicacao?.ativo }
                        }))}
                      >
                        <div className="instrucao-title">
                          <AppIcon name="group_add" />
                          <h4>Programa de Indicação</h4>
                        </div>
                        <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={instrucoesAtendimento.programa_indicacao?.ativo || false}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              programa_indicacao: { ...prev.programa_indicacao, ativo: e.target.checked }
                            }))}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      {instrucoesAtendimento.programa_indicacao?.ativo && (
                        <div className="instrucao-body">
                          <textarea
                            placeholder="Ex: Vale R$15-20 para cliente que trazer novo comprador. O indicado deve mencionar quem indicou na primeira compra..."
                            value={instrucoesAtendimento.programa_indicacao?.texto || ''}
                            onChange={(e) => setInstrucoesAtendimento(prev => ({
                              ...prev,
                              programa_indicacao: { ...prev.programa_indicacao, texto: e.target.value }
                            }))}
                            rows={4}
                            className="textarea-step"
                          />
                          <small>Regras do programa de indicação</small>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ 
                    marginTop: '20px', 
                    padding: '16px', 
                    background: 'rgba(68, 225, 255, 0.05)',
                    border: '1px solid rgba(68, 225, 255, 0.2)',
                    borderRadius: '8px'
                  }}>
                    <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>
                      💡 <strong>Instruções de Atendimento</strong> são injetadas no prompt do agente quando ativas. 
                      Use para personalizar o comportamento do agente de acordo com as necessidades da empresa.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <AppIcon name="sync" className="spinning" />
                Salvando...
              </>
            ) : (
              <>
                <AppIcon name="save" />
                Salvar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
